import React from 'react';
import styles from '~/widgets/Badge/Badge.module.scss';

export type BadgeVariant =
  | 'item'
  | 'ability'
  | 'inline'
  | 'default'
  // Category variants for search modals
  | 'category'
  | 'combat'
  | 'navigation'
  | 'character'
  | 'general';

export type BadgeSize = 'default' | 'sm';

// Category variants that have SCSS module styles
type CategoryVariant = 'category' | 'combat' | 'navigation' | 'character' | 'general';

const CATEGORY_VARIANTS: CategoryVariant[] = ['category', 'combat', 'navigation', 'character', 'general'];

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** When true, applies selected/highlighted styling */
  selected?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Badge component for labels, categories, and tags.
 *
 * Variants:
 * - `item`, `ability`: For item/ability tags
 * - `inline`, `default`: Generic badges
 * - `category`: Gray category badge (like RollSearch)
 * - `combat`, `navigation`, `character`, `general`: Colored category badges
 *
 * The `selected` prop applies highlighted styling for use in selectable lists.
 */
export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'default',
  selected = false,
  children,
  className,
}) => {
  // Check if this is a category-style badge
  const isCategoryVariant = CATEGORY_VARIANTS.includes(variant as CategoryVariant);

  if (isCategoryVariant) {
    const categoryVariant = variant as CategoryVariant;
    const variantClass = styles[categoryVariant];
    const selectedClass = selected ? styles.selected : '';
    const sizeClass = size === 'sm' ? styles.sm : '';

    return (
      <span className={`${styles.categoryBadge} ${variantClass} ${selectedClass} ${sizeClass} ${className || ''}`}>
        {children}
      </span>
    );
  }

  // Legacy inline Tailwind classes for existing variants
  const baseClasses = 'inline-block px-2 py-0.5 rounded text-2xs font-bold uppercase tracking-wider align-middle';

  const variantClasses: Record<string, string> = {
    item: 'bg-item-light text-item border border-item',
    ability: 'bg-ability-light text-ability border border-ability',
    inline: 'mb-0 mx-0.5 bg-app-bg-subtle text-text-secondary border border-app-border',
    default: 'bg-app-bg-subtle text-text-secondary border border-app-border',
  };

  const variantClass = variantClasses[variant] || variantClasses.default;

  return (
    <span className={`${baseClasses} ${variantClass} ${className || ''}`}>
      {children}
    </span>
  );
};
